import React from 'react';
import styled from 'styled-components';
import { Body1, Body5, Container } from '@ui';
import { CloseButton, FlatButton } from '@ui/buttons';
import { ROUTES } from '@constants/routes';
import { createTimeInstance, formatLocalizedDateTime } from '@utils/time';
import {
  DOCUMENT_STATUS_LABELS,
  FILE_FORMAT_LABELS,
  getDocumentFileFormat,
  getDocumentGroupLabel,
} from '@utils/documentVault';
import { DocumentDownloadActions } from '../downloads/DocumentDownloadActions';
import { RenameDocumentField } from '../management/RenameDocumentField';
import { TagDocumentsAction } from '../management/TagDocumentsAction';

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.25);
  z-index: 10000;
`;

const DrawerWrapper = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 420px;
  max-width: 90vw;
  overflow-y: auto;

  background: ${({ theme }) => theme.themeColors.appBackground};
  border-left: 1px solid ${({ theme }) => theme.themeColors.border};
  box-shadow: -2px 0 8px 1px rgba(0, 0, 0, 0.12);
  padding: 24px;
  z-index: 10001;
`;

const CloseButtonWrapper = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;

  svg {
    height: 14px;
    width: 14px;
  }

  g {
    fill: ${({ theme }) => theme.themeColors.text};
  }
`;

const FieldsWrapper = styled.div`
  display: grid;
  grid-template-columns: 120px minmax(0, 1fr);
  gap: 8px;
`;

const Field = ({ label, children }) => (
  <>
    <Body5 isLowContrast>{label}</Body5>
    <Body5>{children}</Body5>
  </>
);

export const DocumentDetailDrawer = ({ doc, status, onClose, mutations }) => {
  // Esc closes without touching the filters behind it.
  React.useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  if (!doc) return null;

  const { filename, doc_type, created_at, access_groups } = doc;
  const uploadedAt = created_at
    ? `${formatLocalizedDateTime('ll2', createTimeInstance(created_at))} ${formatLocalizedDateTime(
        'LT',
        createTimeInstance(created_at)
      )}`
    : '-';

  return (
    <>
      <Backdrop onClick={onClose} />
      <DrawerWrapper role="dialog" aria-label={`Details for ${filename}`}>
        <CloseButtonWrapper>
          <CloseButton onClick={onClose} />
        </CloseButtonWrapper>

        <Container right={32}>
          <Body1 bold>{filename}</Body1>
        </Container>

        <Container top={24}>
          <FieldsWrapper>
            <Field label="Type">{doc_type || '-'}</Field>
            <Field label="Company / tag">{getDocumentGroupLabel(doc)}</Field>
            <Field label="Uploaded">{uploadedAt}</Field>
            <Field label="Status">{DOCUMENT_STATUS_LABELS[status]}</Field>
            <Field label="Format">{FILE_FORMAT_LABELS[getDocumentFileFormat(doc)]}</Field>
            {access_groups?.length ? (
              <Field label="Access groups">{access_groups.map((group) => group.name).join(', ')}</Field>
            ) : null}
          </FieldsWrapper>
        </Container>

        <Container top={24}>
          <FlatButton fullWidth onClick={() => window.open(`${ROUTES.docViewer}?id=${doc.id}`, '_blank')}>
            Open document
          </FlatButton>
        </Container>

        <Container top={24}>
          <Body5 bold>Manage</Body5>
          <Container top={8}>
            <RenameDocumentField
              doc={doc}
              onRename={mutations.renameDocument}
              isSaving={mutations.isSaving}
              error={mutations.error}
            />
          </Container>
          <Container top={12}>
            <TagDocumentsAction
              docs={[doc]}
              onTag={mutations.tagDocuments}
              isSaving={mutations.isSaving}
              error={mutations.error}
            />
          </Container>
        </Container>

        <Container top={24}>
          <Body5 bold>Download</Body5>
          <Container top={8}>
            {/*
              Downloads keep running while the drawer is open -- a clean copy can take minutes,
              and its progress and errors belong next to the document they came from.
            */}
            <DocumentDownloadActions doc={doc} />
          </Container>
        </Container>
      </DrawerWrapper>
    </>
  );
};
